import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://91miaozan.com/',
      },
      ...(category ? [{
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: `http://91miaozan.com/category/${category.slug}`,
      }] : []),
      {
        '@type': 'ListItem',
        position: category ? 3 : 2,
        name: article.title,
        item: `http://91miaozan.com/article/${article.slug}`,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    url: `http://91miaozan.com/article/${article.slug}`,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '爆瓜网',
      url: 'http://91miaozan.com/',
      logo: {
        '@type': 'ImageObject',
        url: 'http://91miaozan.com/logo.png',
      },
    },
    articleSection: category?.name || '娱乐八卦',
    keywords: article.tags.join(','),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <main className="min-h-screen bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]}
          />

          <header className="mt-8 mb-10">
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="font-medium text-foreground-800 whitespace-nowrap">{article.author}</span>
              <span>{article.date}</span>
              <span>{article.views.toLocaleString()} 阅读</span>
              <span>{article.comments.toLocaleString()} 评论</span>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover"
              title={article.title}
            />
          </div>

          <div
            className="article-content max-w-none text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex items-center gap-4">
              <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm cursor-pointer hover:bg-background-200 transition-colors duration-200 whitespace-nowrap">
                <i className="ri-share-forward-line"></i>
                分享文章
              </button>
              <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm cursor-pointer hover:bg-background-200 transition-colors duration-200 whitespace-nowrap">
                <i className="ri-bookmark-line"></i>
                收藏
              </button>
              <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm cursor-pointer hover:bg-background-200 transition-colors duration-200 whitespace-nowrap">
                <i className="ri-chat-1-line"></i>
                评论 ({article.comments.toLocaleString()})
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer hover:bg-background-200/50 transition-colors duration-200"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-foreground-950 text-sm font-medium leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {ra.title}
                      </h3>
                      <p className="text-foreground-400 text-xs mt-2 whitespace-nowrap">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}